import dayjs from 'dayjs'
import {z} from 'zod'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {useTranslation} from '@/translations/i18n'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useEffect, useMemo} from 'react'

const ProjectExpirationDateSchema = ProjectFieldsSchema.pick({
    startDate: true,
    expirationDate: true,
    durationDays: true
})
type ProjectExpirationDateSchema = z.infer<typeof ProjectExpirationDateSchema>

export const ProjectExpirationDateField = () => {
    const {t} = useTranslation()
    const {control, setValue} = useFormContext<ProjectExpirationDateSchema>()
    const {field, fieldState} = useController({control, name: 'expirationDate'})
    const startDate = useWatch({control, name: 'startDate'})
    const durationDays = useWatch({control, name: 'durationDays'})

    const handleChange = (date: Date | null) => {
        field.onChange(date)
        // Calculate duration in days if both dates are available
        if (date && startDate) {
            const durationInDays = dayjs(date).diff(dayjs(startDate), 'day')
            setValue('durationDays', durationInDays, {shouldDirty: true})
        } else if (!date) {
            // Reset duration if expiration date is cleared
            setValue('durationDays', '', {shouldDirty: true})
        }
    }

    const selectedDate = useMemo(() => field.value || null, [field.value])

    useEffect(() => {
        if (startDate && durationDays && !field.value) {
            setValue('expirationDate', dayjs(startDate).add(durationDays, 'day').toDate(), {shouldDirty: true})
        }
    }, [startDate, field.value, durationDays])

    return (
        <InputDate
            required
            label={t('commons:expiration')}
            placeholder="GG/MM/AAAA"
            errorMessage={fieldState.error?.message}
            onChange={handleChange}
            value={selectedDate}
            disabled={!startDate}
            disableBeforeDate={startDate || undefined}
            fullWidth
            onDateOutOfPeriod={() => {
                setValue('expirationDate', new Date(''), {shouldDirty: true})
            }}
        />
    )
}
